'use client';

import Link from 'next/link';
import { usePathname, useRouter } from "next/navigation";
import { twMerge } from 'tailwind-merge';
import ThemedImage from './ThemedImage';
import { FormEvent, HTMLAttributes, ReactNode, forwardRef, useEffect, useRef, useState } from 'react';
import { ILocalizedParams } from '../../models/ICommon';
import { useTheme } from 'next-themes';
import BurgerMenu from './svg/BurgerMenu';
import Close from './svg/Close';
// import { socialIcons, socialsTypeMap } from './svg/socials/SocialsIcons';
import Image, { ImageProps } from 'next/image';
import { IGeneralSettings } from '../../models/IGeneralSettings';
import Arrow from './svg/Arrow';
import { EremSocials } from '../components/EremSocials';
import DarkMode from './svg/Theme/DarkMode';
import LightMode from './svg/Theme/LightMode';
import { scrollTo } from '../../utils/scrollTo';
import { useHash } from '../../hooks/useHash';
import { isBrowser } from '../../utils/isBrowser';
import { IMenuItem } from '../../models/IMenuItem';





interface Props {
  locale: string;
  menu: IMenuItem[];
  staticMenu: {id: string; title: string;}[];
}

export default function Navbar({ locale, staticMenu, settings, isDesktop, menu }: ILocalizedParams<{menu: IMenuItem[]; staticMenu: {title: string; id: string;}[]; settings: IGeneralSettings; isDesktop: boolean; }>) {
  const [reachedScrollThreshhold, setReachedScrollThreshhold] = useState(false);
  const scrollThreshhold = isBrowser() ? (isDesktop ? 100 : 50) : 100;
  const handleScroll = (e: any) => {
    e.preventDefault();
    setReachedScrollThreshhold(window.scrollY >= scrollThreshhold);

  }
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  },[]);
  const hash = useHash();
  useEffect(() => {
    const section = hash.replace("#", "");
    if (section) scrollTo(section);
  }, [hash]);
  return (
    <>
      <DesktopNavbar locale={locale} staticMenu={staticMenu} menu={menu} settings={settings} reachedScrollThreshhold={reachedScrollThreshhold} />
      <MobileNavbar locale={locale} staticMenu={staticMenu} menu={menu} settings={settings} reachedScrollThreshhold={reachedScrollThreshhold}/>
    </>
  )
}

const isSelected = (href: string, pathname: string) => {
  const strippedPath = pathname.split('?')[0];
  strippedPath.replaceAll('/ar', '');
  if (href === '/') return strippedPath === '/';
  else return strippedPath.endsWith(href.startsWith('/') ? href : `/${href}`);
}


function Utils({  className, hideTheme }: {hideTheme?: boolean; className?: string}) {
  const pathname = usePathname();

  return (
    <div className={twMerge('flex items-center [&>*]:cursor-pointer flex-shrink-0', className)}>   
        {hideTheme ? null : <ThemeSwitcher />}

    </div>
  )
}

function Logo({ locale = 'ar', settings, ...props }: { locale?: string } & Partial<ImageProps> & { settings: IGeneralSettings }) {
  const {logo, logoDarkmode} = settings.publisherSettings;
  return (
    <div className='w-fit h-fit grid place-items-center'>
    <a href={'/'} title={'logo'} className={twMerge('cursor-pointer w-[123px] h-[52px] desktop:w-[176px] desktop:h-[76px] flex-shrink-0', props.className)}>
     {[logo,logoDarkmode].every(x => !x) ? null : <ThemedImage src={settings.publisherSettings.logo} darkSrc={settings.publisherSettings.logoDarkmode} alt='logo' eager className={twMerge('h-full w-full cursor-pointer')} loading='eager' priority />}
    </a>
    </div>
  )
}

function DesktopNavbar({ 
  locale = 'ar', 
  staticMenu, 
  menu,
  settings, 
  reachedScrollThreshhold
}: ILocalizedParams<{ 
  menu: IMenuItem[],
  staticMenu: {id: string; title: string;}[]; 
  settings: IGeneralSettings; 
  reachedScrollThreshhold: boolean
}
  >) {

  const now = new Date();
  return (
    <>
       <div className={twMerge('w-full bg-transparent hidden desktop:flex h-[78px] fixed top-0 left-0 z-[15]', reachedScrollThreshhold && 'bg-primaryReal-200 dark:bg-black border-b-[1px] border-b-primaryReal-400 dark:border-b-primaryReal-300')}>
          <div className='max-w-[1296px] w-full mx-auto flex items-center justify-between gap-x-[24px]'>
          
          <div className='flex items-center gap-x-[24px] h-full'>
            <Logo locale={locale} settings={settings} />
            <DesktopMenu locale={locale} staticMenu={staticMenu} menu={menu} settings={settings}/>
          </div>

          <div className='flex items-center gap-x-[24px] h-full'>
            <Utils />
          </div>


      </div>
        </div>
        </>
  )
}


function MobileNavbar({ locale, staticMenu, menu, settings, reachedScrollThreshhold}: ILocalizedParams<{menu: IMenuItem[],staticMenu: {id: string; title: string;}[]; settings: IGeneralSettings; reachedScrollThreshhold: boolean; }>) {

  const [open, setOpen] = useState(false);

  const toggleOpen = () => {
    const newState = !open;
    document.documentElement.style.overflowY = newState ? 'unset' : 'hidden';
    setOpen(newState);
  }


  return (
    <>
    <div className={twMerge('nav-mobile fixed top-0 left-0 z-[1000] w-full flex-shrink-0 flex desktop:hidden items-center justify-between bg-transparent h-[78px] tablet:h-[78px] px-[16px]', (reachedScrollThreshhold || open) && 'bg-primaryReal-200 border-b-[1px] border-b-primaryReal-400 dark:bg-primaryReal-100 dark:border-b-primaryReal-300')}>
      
        <Logo locale={locale} settings={settings} />
          <MobileMenu locale={locale} staticMenu={staticMenu} menu={menu} settings={settings} open={open} toggleOpen={toggleOpen} />
          
    </div>
        </>
  )

}

function MobileMenu({ staticMenu, settings, open, toggleOpen, menu }: Props & { settings: IGeneralSettings; open: boolean; toggleOpen: Function}) {



  const pathname = usePathname();
  
  
  const menuRef = useRef<any>();

  const handleClick = (e: MouseEvent) => {
    const mobileNav = document.querySelector<HTMLElement>('.nav-mobile')!;
    if (open && !menuRef.current?.contains(e.target) && !mobileNav.contains(e.target as any)) toggleOpen();
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClick, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.documentElement.style.overflowY = 'unset';
    };
  });


  return (
    <>
      <div
        className={twMerge('cursor-pointer h-fit flex items-center')}
        onClick={() => toggleOpen()}
      >
        {open ? (
          <div className='flex items-center gap-[24px]'>
            <Utils />
            <Close className='h-[18px] w-[18px]' />
          </div>
         ) : <BurgerMenu className='h-[28px] w-[28px]' />}
      </div>
      {open && <Menu staticMenu={staticMenu} pathname={pathname} menu={menu} settings={settings} toggleOpen={toggleOpen} ref={menuRef}/>}
    </>
  )
}


interface IMenuProps {
  pathname: string; 
  staticMenu: {id: string; title: string;}[]; 
  menu: IMenuItem[];
  toggleOpen: Function;
}

const Menu = forwardRef(({
  staticMenu, 
  pathname,
  toggleOpen,
  settings,
  menu
  }:  IMenuProps & {settings: IGeneralSettings;}, menuRef: any) => {

    const path = usePathname();
    const router = useRouter();
    const isHome = path === '/';

  const handleMenuClick = (id: string) => {
    toggleOpen();
    if (isHome) scrollTo(id);
    else router.push('/');
  }


  return (
  <div
    ref={menuRef}
    className={twMerge('z-[1000] bg-primaryReal-200 dark:bg-primaryReal-100 fixed flex flex-col justify-between dark:left-0 left-[50%] translate-x-[-50%] dark:translate-x-0 border-solid border-b-secondary-400 w-[100vw] top-[78px] tablet:top-[78px] h-[calc(100%-78px)] tablet:h-[calc(100%-78px)] pt-[24px] pb-[48px]')}
    >
        <ul className='flex flex-col h-full overflow-y-auto px-[20px] gap-y-[40px] py-[40px]'>

      {staticMenu.map(i => (
        <li className='w-fit dark:text-white cursor-pointer h4' onClick={() => handleMenuClick(i.id)}>{i.title}</li>
      ))}
      {menu.map(i => (
        <li className='w-fit dark:text-white cursor-pointer h4'><Link href={`${i.targetType === 3 ? '' : '/'}${i.destination}`} onClick={(() => toggleOpen())}>{i.title}</Link></li>
      ))}
      </ul>
    <EremSocials settings={settings} className='flex pt-[32px] justify-center items-center'/>
  </div>
  )
});



function DesktopMenu({ staticMenu, menu, locale }: Props & {settings: IGeneralSettings;}) {
  const path = usePathname();
  const router = useRouter();
  const isHome = path === '/';

  const handleMenuClick = (id: string) => {
    if (isHome) scrollTo(id);
    else router.push('/');
  }

  return (
    <ul className='flex items-center h-full gap-x-[36px] desktop:max-w-[1296px] overflow-visible relative'>
       {staticMenu.map(i => (
        <li className='w-fit dark:text-white cursor-pointer p' onClick={() => handleMenuClick(i.id)}>{i.title}</li>
      ))}
      
       {menu.map(i => (
        <li className='w-fit dark:text-white cursor-pointer p'><Link href={`${i.targetType === 3 ? '' : '/'}${i.destination}`}>{i.title}</Link></li>
      ))}
      

    </ul>
  )
}



function ThemeSwitcher(props: Partial<HTMLAttributes<HTMLElement>>) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
     <div className='grid place-items-center h-[27px] w-[48px] [&>*]:w-full [&>*]:h-full cursor-pointer' {...props} onClick={() => setTheme(isDark ? 'light' : 'dark')}>
        {isDark ? <LightMode /> : <DarkMode/>}
    </div>
  )
}

